import { PeerIdType, PeerMessageType, websocket } from '@playcastdotio/Messaging';
import type { IRealtimeService } from '../../../services/realtime/IRealtimeService';
import type { GameInstanceLifecycle, GameInstanceStore } from '../GameInstanceStore';
import * as Sentry from '@sentry/react';

/**
 * Minimal sink for instance-peer registration. Structural subset of
 * `PeerCoordinator` — keeps this manager testable without the full peer stack.
 */
export type InstancePeerRegistry = {
  registerInstancePeer(peerId: string, gameInstanceIndex: number): void;
  deregisterInstancePeer(peerId: string): void;
};

/**
 * Owns the one-HOST-peer-per-ready-instance topology (Phase 2 AC#5).
 *
 * - When a session reaches `ready` (or beyond) without a peer, requests a
 *   HOST peer id tagged with `peerMetadata.gameInstanceIndex`.
 * - When the API answers (echoing the metadata), registers the
 *   peerId → instance mapping so offers to that peer route to that instance.
 * - Deregisters when the instance is torn down, fails, or enters `restarting`
 *   (android multi-instance host Phase 4: players can't connect to a dead or
 *   rebooting instance; a fresh peer is requested when it returns to `ready`).
 *   Occupied/cooldown instances KEEP their peer — players are (or were)
 *   connected through it.
 */
export class GameInstancePeerManager {
  private readonly peersByIndex = new Map<number, string>();
  private readonly pendingRequests = new Set<number>();
  private unsubscribeStore: (() => void) | null = null;
  private onPeerAssigned: ((index: number) => void) | null = null;

  constructor(
    private readonly store: GameInstanceStore,
    private readonly registry: InstancePeerRegistry,
    private readonly realtimeService: IRealtimeService,
  ) {}

  /** Subscribe to the store and request peers for any already-ready sessions. */
  public attach(onPeerAssigned?: (gameInstanceIndex: number) => void): void {
    if (this.unsubscribeStore) return;
    this.onPeerAssigned = onPeerAssigned ?? null;
    this.unsubscribeStore = this.store.addListener(() => this.reconcile());
    Sentry.logger.info('[GameInstancePeerManager] Attached to store');
    this.reconcile();
  }

  public detach(): void {
    this.unsubscribeStore?.();
    this.unsubscribeStore = null;
    this.onPeerAssigned = null;
  }

  /** Handle a PEER_GET_ID_ANSWER carrying peerMetadata.gameInstanceIndex. */
  public handlePeerIdAssigned(peerId: string, gameInstanceIndex: number): void {
    this.pendingRequests.delete(gameInstanceIndex);
    const session = this.store.getSessionByIndex(gameInstanceIndex);
    if (!session || this.isDead(session.status)) {
      Sentry.logger.warn(`[GameInstancePeerManager] Rejected late peer ${peerId} for index ${gameInstanceIndex} (status=${session?.status ?? 'gone'})`);
      this.registry.deregisterInstancePeer(peerId);
      return;
    }
    const previous = this.peersByIndex.get(gameInstanceIndex);
    if (previous && previous !== peerId) {
      this.registry.deregisterInstancePeer(previous);
    }
    this.peersByIndex.set(gameInstanceIndex, peerId);
    this.registry.registerInstancePeer(peerId, gameInstanceIndex);
    Sentry.logger.info(`[GameInstancePeerManager] Registered peer ${peerId} for instance ${gameInstanceIndex} (status=${session.status})`);
    this.onPeerAssigned?.(gameInstanceIndex);
  }

  public getPeerIdForInstance(gameInstanceIndex: number): string | undefined {
    return this.peersByIndex.get(gameInstanceIndex);
  }

  private reconcile(): void {
    const sessions = this.store.getSessions();
    const liveIndices = new Set(sessions.map((s) => s.gameInstanceIndex));

    for (const session of sessions) {
      // Register on `ready` (or later live states); launching instances wait.
      const peerEligible =
        session.status === 'ready' || session.status === 'occupied' || session.status === 'cooldown';
      const needsPeer =
        peerEligible
        && !this.peersByIndex.has(session.gameInstanceIndex)
        && !this.pendingRequests.has(session.gameInstanceIndex);
      if (needsPeer) {
        this.pendingRequests.add(session.gameInstanceIndex);
        Sentry.logger.info(`[GameInstancePeerManager] Requesting peer for instance ${session.gameInstanceIndex} (status=${session.status})`);
        this.requestInstancePeer(session.gameInstanceIndex);
      }
    }

    for (const [index, peerId] of [...this.peersByIndex]) {
      const session = sessions.find((s) => s.gameInstanceIndex === index);
      const gone = !liveIndices.has(index);
      const dead = session !== undefined && this.isDead(session.status);
      if (gone || dead) {
        this.peersByIndex.delete(index);
        this.registry.deregisterInstancePeer(peerId);
      }
    }

    for (const index of [...this.pendingRequests]) {
      const session = sessions.find((s) => s.gameInstanceIndex === index);
      const dead = session !== undefined && this.isDead(session.status);
      if (!liveIndices.has(index) || dead) {
        this.pendingRequests.delete(index);
      }
    }
  }

  /** Instances players cannot connect to: dead, tearing down, or rebooting. */
  private isDead(status: GameInstanceLifecycle): boolean {
    return status === 'failed' || status === 'stopping' || status === 'restarting';
  }

  private requestInstancePeer(gameInstanceIndex: number): void {
    this.realtimeService.sendMessage(
      websocket.generators.peer(PeerMessageType.PEER_GET_ID, {
        peerIdType: PeerIdType.HOST,
        peerMetadata: { gameInstanceIndex },
      }),
    );
  }
}
